/**
 * Processes newline-delimited JSON from a streaming XHR response.
 *
 * Tracks which lines have already been processed via `state.currentLine`.
 * When the last line fails to parse (likely incomplete mid-stream), it is
 * NOT marked as processed so it will be retried on the next progress event
 * when more data has arrived.
 */
export function processStreamingLines(
  response: string,
  state: { currentLine: number },
  // oxlint-disable-next-line typescript/no-explicit-any
  onMessage: (parsed: any) => void,
): void {
  const lines = response.split('\n');

  for (let index = 0; index < lines.length; index++) {
    const line = lines[index].trim();
    if (line.length === 0) {
      continue;
    }

    // skip lines that have already been processed
    if (index <= state.currentLine) {
      continue;
    }

    try {
      const parsed = JSON.parse(line);
      // Only advance currentLine after a successful parse.
      // The last line mid-stream may be incomplete — by not advancing,
      // we'll retry it on the next progress event when it's complete.
      state.currentLine = index;
      onMessage(parsed);
    } catch {
      // If this isn't the last line, it's a genuine parse error — log and skip.
      if (index < lines.length - 1) {
        // oxlint-disable-next-line no-console
        console.error('Error parsing message from server:', line);
        state.currentLine = index;
      }
      // Otherwise it's likely an incomplete line mid-stream — leave
      // currentLine unchanged so we retry on the next progress event.
    }
  }
}
